import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  signOut,
  fetchAuthSession,
  updateUserAttributes,
  signIn,
  getCurrentUser,
  updatePassword,
  fetchUserAttributes,
} from "aws-amplify/auth";
import { useAuth } from "../context/AuthProvider";
import { modal } from "../modal/ModalService";
import { isValidPassword, LoadingElement, toE164Kr } from "../helpers/utils";
import _ from "lodash";
import {
  KAKAO,
  LOG_IN_COOKIE,
  MY_SHOP_URL_PATH,
  NAVER,
  REST_API_URL,
} from "../constants";
import API from "../service/API";
import { deleteCookie, getCookie, setCookie } from "../helpers/cookie";
import { useLang } from "../context/LangProvider";
import LanguageDropdown from "../helpers/LanguageDropdown";

const PasswordMode = React.memo(
  ({ passwords, setPasswords, onPasswordSave, onCancel, busy, i18n }) => {
    let savable =
      passwords?.current &&
      passwords?.new &&
      passwords?.re &&
      isValidPassword(passwords.new) &&
      passwords.new === passwords.re;

    return (
      <>
        <form className="login__form" style={{ marginTop: "30px" }}>
          <div className="login__label h6" htmlFor="password-current">
            {i18n("mypage.password.current.label")}
          </div>
          <input
            id="password-current"
            type="password"
            className="login__input"
            placeholder={i18n("mypage.password.current.placeholder")}
            value={passwords?.current || ""}
            onChange={(e) =>
              setPasswords({ ...passwords, current: e.target.value })
            }
            autoComplete="current-password"
          />

          <div className="login__label h6" htmlFor="password-new">
            {i18n("mypage.password.new.label")}
          </div>
          <input
            id="password-new"
            type="password"
            className="login__input"
            placeholder={i18n("mypage.password.new.placeholder")}
            value={passwords?.new || ""}
            onChange={(e) =>
              setPasswords({ ...passwords, new: e.target.value })
            }
            autoComplete="new-password"
          />

          <div className="login__label h6" htmlFor="password-re">
            {i18n("mypage.password.confirm.label")}
          </div>
          <input
            id="password-re"
            type="password"
            className="login__input"
            placeholder={i18n("mypage.password.confirm.placeholder")}
            value={passwords?.re || ""}
            onChange={(e) => setPasswords({ ...passwords, re: e.target.value })}
            autoComplete="new-password"
          />

          <button
            type="button"
            onClick={onPasswordSave}
            disabled={!savable || busy}
            aria-disabled={!savable || busy}
            className="login__btn login__btn--primary"
            style={{ marginTop: "80px" }}
          >
            {i18n("common.ok")}
          </button>

          <button
            type="button"
            className="link-btn"
            disabled={busy}
            onClick={onCancel}
            style={{ marginTop: "20px" }}
          >
            {i18n("mypage.password.cancel")}
          </button>
        </form>
      </>
    );
  }
);

const AuthoizeRender = React.memo(
  ({
    attrs,
    onAuthoize,
    setPassword,
    password,
    busy,
    isSocial,
    i18n,
    onKeyDown,
  }) => {
    return !isSocial ? (
      <>
        <div style={{ marginTop: "50px" }}>
          <div className="h4 bold">{i18n("mypage.auth.recheck.title")}</div>
          <p>{i18n("mypage.auth.recheck.desc")}</p>
        </div>

        <form className="login__form" style={{ marginTop: "30px" }}>
          {/* 아이디 */}
          <div className="login__label h6" htmlFor="email">
            {i18n("signup.form.email.label")}
          </div>
          <input
            id="email"
            type="email"
            className="login__input"
            placeholder={i18n("signup.form.email.placeholder")}
            value={attrs.email}
            disabled
            aria-disabled={true}
            autoComplete="email"
          />

          <div className="login__label h6" htmlFor="password">
            {i18n("signup.form.password.label")}
          </div>
          <input
            id="password"
            type="password"
            className="login__input"
            placeholder={i18n("signin.form.password.placeholder")}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            onKeyDown={onKeyDown}
          />

          <button
            type="button"
            onClick={onAuthoize}
            disabled={!password || busy}
            aria-disabled={!password || busy}
            className="login__btn login__btn--primary"
            style={{ marginTop: "80px" }}
          >
            {i18n("common.ok")}
          </button>
        </form>
      </>
    ) : (
      <>
        <div style={{ marginTop: "50px" }}>
          <div className="h4 bold">
            {i18n("mypage.auth.loginRecheck.title")}
          </div>
          <p>{i18n("mypage.auth.loginRecheck.desc")}</p>
        </div>

        <form className="login__form" style={{ marginTop: "30px" }}>
          {/* 소셜일 때는 이메일 입력 숨김 (기존 로직 유지) */}
          {!isSocial ? (
            <>
              <div className="login__label h6" htmlFor="email">
                {i18n("signup.form.email.label")}
              </div>
              <input
                id="email"
                type="email"
                className="login__input"
                placeholder={i18n("signup.form.email.placeholder")}
                value={attrs.email}
                disabled
                aria-disabled={true}
                autoComplete="email"
              />
            </>
          ) : null}

          <button
            type="button"
            onClick={onAuthoize}
            disabled={!isSocial && !password}
            aria-disabled={!isSocial && !password}
            className="login__btn login__btn--primary"
            style={{ marginTop: "80px" }}
          >
            {i18n(`social.${isSocial?.toLowerCase() || ""}`)}
            {i18n("mypage.auth.social.verify")}
          </button>
        </form>
      </>
    );
  }
);

export default function MyPage() {
  const { user, loading } = useAuth();
  const { i18n, cafe24RedirectUrl } = useLang();
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [password, setPassword] = useState("");
  const [isEdit, setEdit] = useState(false);
  const [editing, setEditing] = useState({});
  const [passwordMode, setPasswordMode] = useState(false);
  const [passwords, setPasswords] = useState({});
  const [account, setAccount] = useState(_.cloneDeep(user));

  const isSocial = account?.attributes?.["custom:provider"];
  let attrs = account?.attributes || {};

  const userKey = user?.sub;
  useEffect(() => {
    setConfirmed(false);
  }, [userKey]);

  const refresh = async () => {
    try {
      const me = await getCurrentUser();
      await fetchAuthSession({ forceRefresh: true });
      const attrs = await fetchUserAttributes();
      setAccount((u) => ({ ...u, ...me, attributes: attrs }));
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (window.Kakao && !window.Kakao.isInitialized()) {
      window.Kakao.init(KAKAO.API_KEY);
    }
    getKakaoSession();
    getNaverSession();
    return () => {
      delete window.result;
    };
  }, []);

  const load = useCallback(async () => {
    try {
      const s = await fetchAuthSession();
      const ph = s.tokens?.idToken?.payload?.phone_number || "";
      const em = s.tokens?.idToken?.payload?.email || "";
    } catch (e) {
      modal.alert({
        message: e?.message || String(e),
        okText: "확인",
        backdropClose: true,
      });
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleUpdate = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {};
      payload.name = editing.name;
      payload.birthdate = editing.birthdate;

      await updateUserAttributes({ userAttributes: payload });
      await API.syncUser(user.username);
      await modal.alert({
        message: i18n("mypage.profile.update.success"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
      await refresh();
      await load();
    } catch (err) {
      modal.alert({
        message: err?.message || String(err),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    } finally {
      setEdit(false);
      setEditing({});
      setBusy(false);
    }
  };

  const handlePasswordChangeTry = () => {
    setPasswordMode(true);
  };

  const handlePasswordSave = async () => {
    try {
      await updatePassword({
        oldPassword: passwords.current,
        newPassword: passwords.new,
      });
      modal.alert({
        message: i18n("mypage.password.update.success"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
      setPasswords({});
      setPasswordMode(false);
    } catch (error) {
      modal.alert({
        message: i18n("mypage.password.update.fail"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    }
  };

  const handleBack = async () => {
    window.location.replace(cafe24RedirectUrl + MY_SHOP_URL_PATH);
  };

  const handleSignOutTry = async () => {
    const ok = await modal.confirm({
      message: i18n("signin.confirm.signout"),
      okText: i18n("common.ok"),
      cancelText: i18n("common.cancel"),
      backdropClose: false,
    });
    if (ok) {
      onSignOut();
    }
  };

  const handleDropoutTry = () => {
    nav("/drop-out");
  };

  const onSignOut = async () => {
    setBusy(true);
    try {
      await signOut();
      await refresh();
      nav("/");
    } catch (e) {
      modal.alert({
        message: e?.message || String(e),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    } finally {
      setBusy(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      handleAuthorize();
    }
  };

  const handleAuthorize = async () => {
    const _username = _.cloneDeep(user.username);
    setBusy(true);
    try {
      await getCurrentUser();
      await signOut();
    } catch {}
    try {
      await signIn({ username: _username, password });
      setConfirmed(true);
      setBusy(false);
    } catch (error) {
      setBusy(false);
      modal.alert({
        message: i18n("auth.password.incorrect"),
        okText: i18n("common.ok"),
        backdropClose: true,
      });
    }
  };

  const getKakaoSession = async () => {
    const cookie = getCookie(LOG_IN_COOKIE);
    if (cookie && cookie === "kakao") {
      fetch(`${REST_API_URL}${KAKAO.SESSION_PATH}`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.authenticated) {
            deleteCookie(LOG_IN_COOKIE);
            setConfirmed(true);
          }
        });
    }
  };

  const getNaverSession = async () => {
    const cookie = getCookie(LOG_IN_COOKIE);
    if (cookie && cookie === "naver") {
      fetch(`${REST_API_URL}${NAVER.SESSION_PATH}`, {
        method: "GET",
        credentials: "include",
      })
        .then((res) => res.json())
        .then((data) => {
          if (data?.authenticated) {
            deleteCookie(LOG_IN_COOKIE);
            setConfirmed(true);
          }
        });
    }
  };

  const handleSocialAuthorize = () => {
    if (isSocial === "KAKAO") {
      if (!window.Kakao) return;
      setCookie(LOG_IN_COOKIE, "kakao");
      window.Kakao.Auth.authorize({
        redirectUri: `${REST_API_URL}${KAKAO.CALLBACK_PATH}`,
      });
    } else {
      setCookie(LOG_IN_COOKIE, "naver");
      const redirectUri = `${REST_API_URL}${NAVER.CALLBACK_PATH}`;
      const state = crypto.randomUUID();

      const naverAuthUrl = `${NAVER.AUTH_URL}?response_type=code&client_id=${
        NAVER.CLIENT_ID
      }&redirect_uri=${encodeURIComponent(redirectUri)}&state=${state}`;

      window.location.href = naverAuthUrl;
    }
  };

  const handleEditPress = () => {
    setEditing(_.cloneDeep(attrs));
    setEdit(true);
  };

  if (loading || !user) return <LoadingElement />;

  return (
    <div className="my-page">
      <div className="topbar__inner">
        <button
          type="button"
          className="topbar__back"
          onClick={handleBack}
          style={{ marginBottom: 0 }}
          aria-label={i18n("mypage.nav.backShop.aria")}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
            <path
              d="M15 18l-6-6 6-6"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="topbar__back-text h6">
            {i18n("mypage.nav.backShop")}
          </div>
        </button>
        <LanguageDropdown />
      </div>

      <div className="flex-center" style={{ marginTop: "30px" }}>
        <img
          alt="logo"
          src={require("../img/granhand_logo.png")}
          className="top-logo"
        />
      </div>

      {confirmed ? (
        passwordMode ? (
          <PasswordMode
            setPasswords={setPasswords}
            passwords={passwords}
            onPasswordSave={handlePasswordSave}
            busy={busy}
            i18n={i18n}
            onCancel={() => {
              setPasswords({});
              setPasswordMode(false);
            }}
          />
        ) : (
          <>
            <div
              className="column-center"
              style={{ marginTop: "50px", gap: "15px" }}
            >
              <div className="h6 brownDark70-color">
                {attrs.email || "email"}
              </div>

              {isSocial ? (
                <div className="h7 brownDark70-color bold">
                  {i18n(`social.${isSocial?.toLowerCase() || ""}`)}
                  {i18n("mypage.social.loggedInSuffix")}
                </div>
              ) : (
                <div
                  onClick={handlePasswordChangeTry}
                  className="find-password-btn"
                >
                  {i18n("mypage.password.change")}
                </div>
              )}
            </div>

            <form style={{ marginTop: "30px" }}>
              <div className="h5 bold">{i18n("mypage.profile.title")}</div>

              <div className="login__label h6" htmlFor="name">
                {i18n("mypage.profile.name.label")}
              </div>
              <input
                id="name"
                className="login__input"
                placeholder={i18n("mypage.profile.name.placeholder")}
                value={isEdit ? editing.name : attrs?.name}
                disabled
                aria-disabled={true}
                onChange={(e) =>
                  setEditing({ ...editing, name: e.target.value })
                }
              />

              <div className="login__label h6" htmlFor="birth">
                {i18n("signup.form.birth.label")}
              </div>
              <input
                id="birth"
                type="date"
                className="login__input"
                placeholder={i18n("signup.form.birth.placeholder")}
                disabled={!isEdit}
                aria-disabled={!isEdit}
                value={isEdit ? editing.birthdate : attrs.birthdate}
                onChange={(e) =>
                  setEditing({ ...editing, birthdate: e.target.value })
                }
              />

              <div className="login__label h6" htmlFor="phone">
                {i18n("mypage.profile.phone.label")}
              </div>
              <input
                id="phone"
                type="tel"
                className="login__input"
                value={attrs.phone_number}
                disabled
                aria-disabled={true}
              />

              <button
                type="button"
                onClick={isEdit ? handleUpdate : handleEditPress}
                disabled={
                  isEdit && (!editing?.birthdate || !editing?.name || busy)
                }
                aria-disabled={
                  isEdit && (!editing?.birthdate || !editing?.name || busy)
                }
                className="login__btn login__btn--primary"
                style={{ marginTop: "30px" }}
              >
                {isEdit
                  ? i18n("mypage.profile.save")
                  : i18n("mypage.profile.edit")}
              </button>

              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "24px",
                  marginTop: "24px",
                }}
              >
                {/* <button
                  type="button"
                  className="topbar__forward"
                  onClick={handleSignOutTry}
                  aria-label={i18n("signin.confirm.signout")}
                >
                  <span className="topbar__forward-text h6">
                    {i18n("signin.cta.logout")}
                  </span>
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path
                      d="M9 6l6 6-6 6"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button> */}

                <button
                  type="button"
                  className="link-btn"
                  onClick={handleDropoutTry}
                >
                  {i18n("mypage.dropout")}
                </button>
              </div>
            </form>
          </>
        )
      ) : (
        <AuthoizeRender
          user={user}
          attrs={attrs}
          onAuthoize={isSocial ? handleSocialAuthorize : handleAuthorize}
          password={password}
          setPassword={setPassword}
          isSocial={isSocial}
          onKeyDown={handleKeyDown}
          i18n={i18n}
          busy={busy}
        />
      )}
    </div>
  );
}
